import { isUntracked } from "./dayMarks";
import { age } from "./body";
import { addDays, dayLabel, parseDay } from "./date";
import { basedOn, type Insight } from "./insight";
import { curWeight } from "./energy";
import { foodOn } from "./intake";
import { activeMinutes } from "./training";
import type { DayLog, KayaState, Profile, WaterBottle, WaterEntry, WaterKind, WaterSettings } from "./types";

/*
 * Water: a target that counts drinks, and the reminder arithmetic (ADR-044).
 *
 * The target is for drinks, not total water — food brings its own, and no app can measure it.
 * Every drink counts at its volume (Maughan 2016: tea, coffee and milk are retained like water),
 * so the kind is only a label. Pure: the server's push scheduler reuses the reminder functions.
 */

export const ML_PER_KG = 30;
/** ESPEN/EFSA's adequate intake × 0.8 — the part that comes from drinks. Also the whole target at 60+. */
export const WATER_FLOOR_ML = { female: 1600, male: 2000 } as const;
export const WATER_CAP_ML = 3000;
/** ICMR's "about 2 L of drinks" — the figure when the sex is not known. */
export const ICMR_ML = 2000;
export const OLDER_AGE = 60;
/** per hour of the day's real training minutes — inside ACSM's range, and called an estimate */
export const WORK_ML_PER_HOUR = 500;
/** Kaya's own figure for a hot day, switched on by hand; there is no weather feed offline */
export const HOT_ML = 500;
/** EFSA's additions */
export const PREGNANT_ML = 300;
export const BREASTFEEDING_ML = 700;
/** ICMR's glass */
export const GLASS_ML = 250;
export const SIP_MIN_ML = 30;
export const SIP_MAX_ML = 100;
/** The parents' reminders: fixed, fewer, and none after 19:00 — drinking at night in older age
 *  means getting up at night, and that means falls. */
export const OLDER_TIMES = ["09:00", "11:30", "14:00", "16:30", "19:00"];
export const OLDER_LAST = "19:00";

export const WATER_DEFAULTS: WaterSettings = {
  remindOn: false,
  from: "08:00",
  to: "21:00",
  everyMin: 90,
  skipMin: 45,
  stopAtTarget: true,
  quiet: true,
  sip: 50,
  bottles: [],
  at: 0,
};

export const WATER_KINDS: { value: WaterKind; label: string }[] = [
  { value: "water", label: "Water" },
  { value: "chai", label: "Chai" },
  { value: "milk", label: "Milk" },
  { value: "chaas", label: "Chaas" },
  { value: "nimbu", label: "Nimbu pani" },
  { value: "coconut", label: "Coconut water" },
  { value: "coffee", label: "Coffee" },
  { value: "other", label: "Other" },
];

export const KIND_LABEL: Record<WaterKind, string> = Object.fromEntries(
  WATER_KINDS.map((k) => [k.value, k.label])
) as Record<WaterKind, string>;

/** The settings with every default filled in — a blob may be sparse (invariant 3). */
export function waterSettings(S: KayaState): WaterSettings {
  const w = S.profile.water;
  return { ...WATER_DEFAULTS, ...(w || {}), bottles: w?.bottles || [] };
}

/* ---------- what the questionnaire will know ---------- */

/**
 * The pregnancy answer and the conditions, read from the questionnaire's `profile.eating` block.
 *
 * That block is being built separately (plan §3.4); until it lands in `types.ts` this reads it
 * structurally, so a profile without it simply has no answers. The one place water touches it.
 */
interface EatingAnswers {
  pregnant?: unknown;
  conditions?: unknown;
}

function eatingOf(p: Profile): EatingAnswers {
  const e = (p as Profile & { eating?: unknown }).eating;
  return e && typeof e === "object" ? (e as EatingAnswers) : {};
}

function conditionsOf(p: Profile): string[] {
  const c = eatingOf(p).conditions;
  return Array.isArray(c) ? c.filter((x): x is string => typeof x === "string").map((x) => x.toLowerCase()) : [];
}

const hasCondition = (p: Profile, ...words: string[]) =>
  conditionsOf(p).some((c) => words.some((w) => c.includes(w)));

/** Kidney disease or heart failure: how much to drink is the doctor's call, not a formula's. */
export function fluidRestricted(S: KayaState): boolean {
  return hasCondition(S.profile, "kidney", "ckd", "heart", "hf");
}

/** Coconut water is high in potassium (ICMR p. 89), so it is not offered with a kidney condition
 *  or high potassium. */
export function waterKinds(S: KayaState): { value: WaterKind; label: string }[] {
  const hide = hasCondition(S.profile, "kidney", "ckd", "potassium", "hyperkal");
  return hide ? WATER_KINDS.filter((k) => k.value !== "coconut") : WATER_KINDS;
}

function pregnancyOf(p: Profile): "pregnant" | "breastfeeding" | null {
  const v = eatingOf(p).pregnant;
  return v === "pregnant" || v === "breastfeeding" ? v : null;
}

function sexOf(S: KayaState): "male" | "female" | null {
  const s = S.profile.sex;
  return s === "male" || s === "female" ? s : null;
}

export function isOlder(S: KayaState, ds: string): boolean {
  const a = age(S, parseDay(ds));
  return a !== null && a >= OLDER_AGE;
}

/* ---------- formatting ---------- */

export const round100 = (ml: number) => Math.round(ml / 100) * 100;

/** A target or an average: one decimal — "2.6". */
export function litres(ml: number): string {
  return (ml / 1000).toFixed(1);
}

/** What was drunk: up to two decimals, so a glass shows — "1.35", "1.3", "0.85". */
export function drunkLitres(ml: number): string {
  if (ml === 0) return "0";
  const s = (ml / 1000).toFixed(2);
  return s.endsWith("0") ? s.slice(0, -1) : s;
}

/** "250 ml", "1,000 ml" */
export const mlText = (v: number) => `${Math.round(v).toLocaleString("en-IN")} ml`;

/* ---------- the target ---------- */

export type WaterTargetKind =
  /** 30 ml per kg, inside the bounds */
  | "perKg"
  /** 30 ml per kg fell under the floor, so the floor */
  | "floor"
  /** 30 ml per kg went past 3.0 L, so 3.0 L */
  | "cap"
  /** 60+: ESPEN's figure alone, no per-kg scaling */
  | "older"
  /** no sex in the profile: ICMR's 2.0 L */
  | "general"
  /** a doctor's limit: a ceiling, nothing added */
  | "limit"
  /** 60+ on (or unsure about) a water tablet: pinned to the floor, nothing added */
  | "diuretic"
  /** a kidney or heart condition with no limit typed: no figure at all */
  | "doctor";

/** One row of the evidence: what it rests on, and what it adds. */
export interface WaterLine {
  label: string;
  value: string;
  est?: boolean;
}

export interface WaterTarget {
  /** null only for `doctor`: Kaya will not invent a figure a doctor should set */
  ml: number | null;
  kind: WaterTargetKind;
  /** the one sentence Food › Water shows */
  text: string;
  lines: WaterLine[];
  /** "the Mon 21 Sep weigh-in and 2 walks" — the evidence line after "based on" */
  basedOn: string;
  /** Work was added — an estimate */
  est: boolean;
  /** a limit, not a goal: nothing on screen may ask for more */
  ceiling: boolean;
  /** whether Work and a hot day may add to it — the hot-day switch is offered only then */
  additions: boolean;
  /** the salt-level line, for a water tablet at 60+ */
  caution: string | null;
  base: number | null;
  work: number;
  hot: number;
  pregnancy: number;
  /** the day's active minutes */
  minutes: number;
}

export const DIURETIC_LINE =
  "Water tablets and a lot of water can lower your salt level. Ask your doctor what's right for you.";

const ICMR_LINE: WaterLine = { label: "ICMR: drinks for adults, more in heat and hard work", value: "about 2 L" };
const NOT_ADDED: WaterLine = { label: "Walks and hot days", value: "not added" };

/** Whether the day's work was all walking, so the words can say "walking" rather than "training". */
function workWords(L: DayLog | undefined): { noun: string; label: string; walks: number } {
  const walks = L?.walks?.length ?? 0;
  const other = !!(L && ((L.elapsedMs || 0) > 0 || (L.extraDoneMs || 0) > 0 || (L.extraElapsed || 0) > 0 || L.treadmill));
  return walks && !other
    ? { noun: "walking", label: "Walks today", walks }
    : { noun: "training", label: "Training today", walks };
}

function weighInWords(S: KayaState): string {
  const ms = S.measures.filter((m) => m.w).sort((a, b) => (a.date < b.date ? -1 : 1));
  if (!ms.length) return "your starting weight";
  const d = ms[ms.length - 1].date;
  return `the ${dayLabel(d)} weigh-in`;
}

/**
 * The day's drinks target (water.md §4.1).
 *
 * 30 ml × kg, bounded below by ESPEN's floor × 0.8 and above by 3.0 L; at 60+ the floor alone.
 * Then +500 ml per hour of the day's real training minutes (rounded to 100), +500 ml on a hot
 * day, +300 / +700 ml in pregnancy / breastfeeding; the total rounded to 100 ml. The safety
 * states come first: a doctor's limit replaces all of it, a water tablet at 60+ pins the floor,
 * and a kidney or heart condition without a limit has no figure.
 *
 * Null when the formula needs a weight and there is none.
 */
export function waterTarget(S: KayaState, ds: string): WaterTarget | null {
  const set = waterSettings(S);
  const L = S.logs[ds];
  const minutes = activeMinutes(S, ds);
  const blank = { base: null, work: 0, hot: 0, pregnancy: 0, minutes, est: false, caution: null };

  if (set.limitMl && set.limitMl > 0) {
    const v = round100(set.limitMl);
    return {
      ...blank,
      ml: v,
      kind: "limit",
      text: `${litres(v)} L: your doctor's limit. Kaya adds nothing to it and never asks for more.`,
      lines: [{ label: "Your doctor's limit", value: `${litres(v)} L` }, NOT_ADDED],
      basedOn: "the limit you entered",
      ceiling: true,
      additions: false,
    };
  }
  if (fluidRestricted(S)) {
    return {
      ...blank,
      ml: null,
      kind: "doctor",
      text: "With a kidney or heart condition, how much to drink is your doctor's call. Add their limit to track against it.",
      lines: [],
      basedOn: "the conditions you listed",
      ceiling: true,
      additions: false,
    };
  }

  const sex = sexOf(S);
  const older = isOlder(S, ds);
  const yearsOld = age(S, parseDay(ds));
  const who = sex === "female" ? "women" : "men";

  if (older && sex && (set.diuretic === "yes" || set.diuretic === "unsure")) {
    const v = WATER_FLOOR_ML[sex];
    return {
      ...blank,
      base: v,
      ml: v,
      kind: "diuretic",
      text: `${litres(v)} L: the figure for ${who} over 60 (ESPEN). Kaya does not scale it by weight at your age.`,
      lines: [{ label: `ESPEN, ${who} over 60`, value: `${litres(v)} L` }, NOT_ADDED],
      basedOn: `your age, ${yearsOld}, and your answer about water tablets`,
      ceiling: false,
      additions: false,
      caution: DIURETIC_LINE,
    };
  }

  // the base, and the words for it
  let base: number;
  let kind: WaterTargetKind;
  let baseText: string;
  let baseLine: WaterLine;
  let basis: string;
  let tail = "";
  const kg = curWeight(S);
  if (older && sex) {
    base = WATER_FLOOR_ML[sex];
    kind = "older";
    baseText = `the figure for ${who} over 60 (ESPEN`;
    baseLine = { label: `ESPEN, ${who} over 60`, value: `${litres(base)} L` };
    basis = `your age, ${yearsOld}`;
    tail = " Kaya does not scale it by weight at your age.";
  } else if (!sex) {
    base = ICMR_ML;
    kind = "general";
    baseText = "ICMR's figure for adults";
    baseLine = { label: "ICMR, adults", value: `${litres(base)} L` };
    basis = "ICMR's adult figure — add your sex in You › Body for your own";
  } else {
    if (!(kg > 0)) return null;
    const raw = ML_PER_KG * kg;
    const at = `30 ml per kg at ${Math.round(kg * 10) / 10} kg`;
    basis = weighInWords(S);
    if (raw < WATER_FLOOR_ML[sex]) {
      base = WATER_FLOOR_ML[sex];
      kind = "floor";
      baseText = `the least for ${who} (${litres(base)} L), as ${at} is only ${litres(raw)} L`;
      baseLine = { label: `${at}, raised to the least for ${who}`, value: `${litres(base)} L` };
    } else if (raw > WATER_CAP_ML) {
      base = WATER_CAP_ML;
      kind = "cap";
      baseText = `the ${litres(base)} L cap, as ${at} would be ${litres(raw)} L`;
      baseLine = { label: `${at}, capped`, value: `${litres(base)} L` };
    } else {
      base = raw;
      kind = "perKg";
      baseText = at;
      baseLine = { label: at, value: `${litres(base)} L` };
    }
  }

  const words = workWords(L);
  const work = round100((WORK_ML_PER_HOUR * minutes) / 60);
  const hot = L?.hot ? HOT_ML : 0;
  const preg = older ? null : pregnancyOf(S.profile);
  const pregnancy = preg === "pregnant" ? PREGNANT_ML : preg === "breastfeeding" ? BREASTFEEDING_ML : 0;
  const total = round100(base + work + hot + pregnancy);

  const adds: string[] = [];
  if (work) adds.push(`+${work} ml for today's ${minutes} minutes of ${words.noun}`);
  if (hot) adds.push(`+${hot} ml for a hot day`);
  if (pregnancy) adds.push(`+${pregnancy} ml while ${preg}`);

  // "2.6 L: 30 ml per kg at 76.6 kg (2.3 L), +300 ml for …" — the base in brackets only when
  // something was added to it (the floor and the cap already name theirs); ESPEN's bracket is open.
  let head = `${litres(total)} L: ${baseText}`;
  if (kind === "older") head += adds.length ? `, ${litres(base)} L)` : ")";
  else if (adds.length && (kind === "perKg" || kind === "general")) head += ` (${litres(base)} L)`;
  const text = `${head}${adds.length ? `, ${adds.join(", ")}` : ""}.${tail}`;

  const lines: WaterLine[] = [baseLine];
  lines.push(
    minutes
      ? { label: `${words.label}, ${minutes} min`, value: `+${work} ml`, est: true }
      : { label: "Training today", value: "none yet" }
  );
  lines.push({ label: "Hot day", value: hot ? `+${hot} ml` : "off" });
  if (pregnancy) lines.push({ label: preg === "pregnant" ? "Pregnant" : "Breastfeeding", value: `+${pregnancy} ml` });
  if (kind !== "older") lines.push(ICMR_LINE);

  const trained =
    minutes > 0
      ? words.noun === "walking"
        ? ` and ${words.walks} walk${words.walks === 1 ? "" : "s"}`
        : " and today's training"
      : "";

  return {
    ml: total,
    kind,
    text,
    lines,
    basedOn: `${basis}${trained}`,
    est: work > 0,
    ceiling: false,
    additions: true,
    caution: null,
    base,
    work,
    hot,
    pregnancy,
    minutes,
  };
}

/* ---------- the log ---------- */

export function waterEntries(S: KayaState, ds: string): WaterEntry[] {
  return [...(S.logs[ds]?.water || [])].sort((a, b) => a.at - b.at);
}

/** Millilitres drunk on a day. 0 means nothing logged; `waterEntries` tells the two apart. */
export function waterTotal(L: DayLog | undefined): number {
  return (L?.water || []).reduce((a, e) => a + e.ml, 0);
}

/** "1.35 of 2.6 L" — the row value on Food and Today. */
export function waterRowValue(S: KayaState, ds: string): string {
  const t = waterTarget(S, ds);
  const drunk = drunkLitres(waterTotal(S.logs[ds]));
  return t?.ml ? `${drunk} of ${litres(t.ml)} L` : `${drunk} L`;
}

export interface WaterAverage {
  /** mean ml a logged day */
  drunkMl: number;
  /** mean of the logged days' targets; null when none of them had a figure */
  targetMl: number | null;
  logged: number;
  of: number;
}

/**
 * The mean over the days that have a drink logged — both the drinks and the targets, so the two
 * figures describe the same days. A day with nothing logged is not a day of drinking nothing, so
 * it is left out rather than counted as 0; with none logged the answer is null (invariant 11).
 */
export function waterAverage(S: KayaState, days: string[]): WaterAverage | null {
  const logged = days.filter((ds) => (S.logs[ds]?.water?.length ?? 0) > 0);
  if (!logged.length) return null;
  const drunk = logged.reduce((a, ds) => a + waterTotal(S.logs[ds]), 0) / logged.length;
  const targets = logged.map((ds) => waterTarget(S, ds)?.ml ?? null).filter((v): v is number => v !== null);
  return {
    drunkMl: drunk,
    targetMl: targets.length ? targets.reduce((a, v) => a + v, 0) / targets.length : null,
    logged: logged.length,
    of: days.length,
  };
}

/** "7-day average 1.9 L of 2.5 L" — null with nothing logged. */
export function waterAverageLine(a: WaterAverage | null): string | null {
  if (!a) return null;
  return `7-day average ${litres(a.drunkMl)} L${a.targetMl !== null ? ` of ${litres(a.targetMl)} L` : ""}`;
}

/** The seven days before `today` — today is left out because it is not over. */
export function lastSevenDays(today: string): string[] {
  return [7, 6, 5, 4, 3, 2, 1].map((n) => addDays(today, -n));
}

/**
 * Progress › Food's one water line: "Water: 7-day average 1.9 L of 2.5 L, logged on 6 of 7 days."
 * A count of logged days — never a streak, never "days hit" (water.md §4.3).
 */
export function waterInsight(S: KayaState, today: string): Insight {
  const a = waterAverage(S, lastSevenDays(today));
  const link = { to: "/food/water", label: "Water" };
  if (!a)
    return {
      key: "food.water",
      text: "Water: nothing logged in the last 7 days.",
      basedOn: basedOn("0 logged days", "7 days"),
      link,
    };
  return {
    key: "food.water",
    text: `Water: ${waterAverageLine(a)}, logged on ${a.logged} of ${a.of} days.`,
    basedOn: basedOn(`${a.logged} logged day${a.logged === 1 ? "" : "s"}`, "7 days"),
    link,
  };
}

/** The quarters of a bottle, for the bottle sheet. */
export function bottleParts(b: WaterBottle): { label: string; word: string; frac: number; ml: number }[] {
  return [
    { label: "¼", word: "Quarter", frac: 0.25, ml: Math.round(b.ml / 4) },
    { label: "½", word: "Half", frac: 0.5, ml: Math.round(b.ml / 2) },
    { label: "¾", word: "Three quarters", frac: 0.75, ml: Math.round((b.ml * 3) / 4) },
    { label: "Whole", word: "Whole", frac: 1, ml: b.ml },
  ];
}

export const clampSip = (v: number) =>
  Math.min(SIP_MAX_ML, Math.max(SIP_MIN_ML, Math.round(v / 10) * 10));

/* ---------- reminders (the server's scheduler reuses these) ---------- */

export function toMin(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function fromMin(n: number): string {
  return `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
}

/** "8:00 am", "9:00 pm" */
export function clock12(hhmm: string): string {
  const n = toMin(hhmm);
  const h = Math.floor(n / 60);
  const m = n % 60;
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
}

/**
 * The times a reminder may go out on `ds`: every `everyMin` from `from` up to and including `to`
 * — 08:00 to 21:00 every 90 minutes is nine — or, at 60+, the fixed times, none after 19:00.
 */
export function reminderSlots(S: KayaState, ds: string): string[] {
  const set = waterSettings(S);
  if (isOlder(S, ds)) {
    const times = set.times?.length ? set.times : OLDER_TIMES;
    return [...times].filter((t) => toMin(t) <= toMin(OLDER_LAST)).sort();
  }
  const from = toMin(set.from);
  const to = toMin(set.to);
  const every = Math.max(15, set.everyMin || WATER_DEFAULTS.everyMin);
  const out: string[] = [];
  for (let t = from; t <= to; t += every) out.push(fromMin(t));
  return out;
}

/** Why no reminder goes out on `ds` at all, or null when they may. An untracked day is a day the
 *  person chose not to count (ADR-049), so it is quiet like a travel day. */
export function remindersQuiet(S: KayaState, ds: string): "off" | "food-off" | "travel" | "untracked" | null {
  const set = waterSettings(S);
  if (!set.remindOn) return "off";
  if (set.quiet && !foodOn(S)) return "food-off";
  if (set.quiet && S.travelDates[ds]) return "travel";
  if (set.quiet && isUntracked(S, ds)) return "untracked";
  return null;
}

export interface WaterNudge {
  slot: string;
  title: string;
  body: string;
}

/**
 * The reminder for one slot, or null when it should not go out: reminders off or a quiet day,
 * a drink logged within `skipMin` before it, or the target already met. `lastAt` and the total
 * are read from `S` as it stands, so a caller scheduling ahead re-runs this after every drink.
 *
 * The words follow the safety states: at 60+ only "time for a glass", never an amount left; under
 * a doctor's limit only what has been logged, never a push to drink more.
 */
export function reminderFor(S: KayaState, ds: string, slot: string): WaterNudge | null {
  if (remindersQuiet(S, ds)) return null;
  const set = waterSettings(S);
  const slotMin = toMin(slot);
  const entries = waterEntries(S, ds);
  const last = entries[entries.length - 1];
  if (last) {
    const d = new Date(last.at);
    const lastMin = d.getHours() * 60 + d.getMinutes();
    if (lastMin <= slotMin && slotMin - lastMin < set.skipMin) return null;
  }
  const t = waterTarget(S, ds);
  const total = waterTotal(S.logs[ds]);
  const title = "Water";
  if (t?.ceiling) {
    if (t.ml !== null && total >= t.ml) return null;
    const of = t.ml !== null ? ` of your ${litres(t.ml)} L limit` : "";
    return { slot, title, body: `Logged so far: ${drunkLitres(total)} L${of}.` };
  }
  if (t?.ml && set.stopAtTarget && total >= t.ml) return null;
  if (isOlder(S, ds)) return { slot, title, body: "Time for a glass of water." };
  const so = t?.ml ? `${drunkLitres(total)} of ${litres(t.ml)} L so far. ` : "";
  return { slot, title, body: `${so}Time for a glass.` };
}

/** The reminders still to come on `ds` after `nowMin` (minutes since midnight). */
export function remainingNudges(S: KayaState, ds: string, nowMin: number): WaterNudge[] {
  return reminderSlots(S, ds)
    .filter((s) => toMin(s) > nowMin)
    .map((s) => reminderFor(S, ds, s))
    .filter((n): n is WaterNudge => n !== null);
}

/** "Every 90 min, 8 am – 9 pm" / "5 a day, 9 am to 7 pm" — the Reminders row's value. */
export function remindersSummary(S: KayaState, ds: string): string {
  const set = waterSettings(S);
  if (!set.remindOn) return "Off";
  const short = (t: string) => clock12(t).replace(":00", "");
  const slots = reminderSlots(S, ds);
  if (isOlder(S, ds)) return slots.length ? `${slots.length} a day, ${short(slots[0])} to ${short(slots[slots.length - 1])}` : "None";
  return `Every ${set.everyMin} min, ${short(set.from)} – ${short(set.to)}`;
}

/* ---------- the push server's half (ADR-045) ----------
   The API imports these straight from this file, so the reminder a server sends and the one the
   page would schedule are the same arithmetic. */

/** How long after a slot its push may still go out — a restart or a slow tick loses nothing, and
 *  a reminder later than this is worse than none. */
export const REMINDER_GRACE_MIN = 15;

/**
 * The slot due at `nowMin` on `ds`: the latest one at or before now and less than
 * `graceMin` behind it. Null between slots. Whether it should go out is `reminderFor`'s call;
 * this only says which one it is.
 */
export function dueSlot(S: KayaState, ds: string, nowMin: number, graceMin = REMINDER_GRACE_MIN): string | null {
  const due = reminderSlots(S, ds).filter((s) => toMin(s) <= nowMin && nowMin - toMin(s) < graceMin);
  return due.length ? due[due.length - 1] : null;
}

/** What a push carries. `glass` is the +250 ml button — left off under a doctor's limit, where
 *  nothing on screen may ask for more, not even a button. */
export interface PushNudge {
  title: string;
  body: string;
  /** one tag per kind, so a new reminder replaces an unread one rather than stacking */
  tag: string;
  url: string;
  glass: boolean;
}

export const WATER_TAG = "water";

/** The push for `slot`, or null when `reminderFor` says it should not go out. */
export function pushNudge(S: KayaState, ds: string, slot: string): PushNudge | null {
  const n = reminderFor(S, ds, slot);
  if (!n) return null;
  return {
    title: n.title,
    body: n.body,
    tag: WATER_TAG,
    url: "/food/water",
    glass: !waterTarget(S, ds)?.ceiling,
  };
}

/** A drink logged outside the app — a notification's +250 ml — on its way into the day's log. */
export interface PendingDrink extends WaterEntry {
  ds: string;
}

/** Whether `d` still has to be added: not in its day already, and not taken back since. */
export function drinkIsNew(S: KayaState, d: PendingDrink): boolean {
  const L = S.logs[d.ds];
  if (L?.waterOff && d.id in L.waterOff) return false;
  return !(L?.water || []).some((e) => e.id === d.id);
}

/**
 * `S` with the pending drinks folded in, each at most once. The same drink can arrive twice —
 * from the worker's own queue and from the server's inbox — and one removed since must stay
 * removed, so both are checked by id. Pure: the server reads a day through this before deciding
 * a reminder; the store writes through `toggles.ts::applyDrinks`, which asks `drinkIsNew` too.
 */
export function withDrinks(S: KayaState, drinks: PendingDrink[]): KayaState {
  let logs: KayaState["logs"] | null = null;
  for (const { ds, ...e } of drinks) {
    const cur = logs ? { ...S, logs } : S;
    if (!drinkIsNew(cur, { ds, ...e })) continue;
    logs = logs || { ...S.logs };
    const L: DayLog = logs[ds] || { done: {}, treadmill: null };
    logs[ds] = { ...L, water: [...(L.water || []), e] };
  }
  return logs ? { ...S, logs } : S;
}
